import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceDot, Label } from 'recharts';
import Button from '../../UI/Button';

// Custom label component for multi-line text
const CustomLabel = ({ viewBox }) => {
  const { x, y } = viewBox;
  return (
    <g>
      <text x={x - 35} y={y - 25} fill="#059669" fontSize="11" fontWeight="bold" textAnchor="middle">
        Peak cellular
      </text>
      <text x={x - 35} y={y - 13} fill="#059669" fontSize="11" fontWeight="bold" textAnchor="middle">
        regeneration
      </text>
    </g>
  );
};

// Product-specific timeline content
const timelineContent = {
  nad: {
    title: "How will NAD+ work for you?",
    titleHighlight: "work for you?",
    week1: "Your NAD+ levels begin to restore",
    week4: "Energy production increases dramatically",
    week9: "Your cells have become age-defying powerhouses",
    footer: "We address the root causes of cellular aging, so you get a long-term solution, not just another quick fix."
  },
  glutathione: {
    title: "How will Glutathione work for you?",
    titleHighlight: "work for you?",
    week1: "Antioxidant protection begins",
    week4: "Skin brightening becomes visible",
    week9: "Full-body detox and radiant glow achieved",
    footer: "We address the root causes of oxidative stress, so you get a long-term solution, not just another quick fix."
  },
  vitaminb12: {
    title: "How will B12 work for you?",
    titleHighlight: "work for you?",
    week1: "Energy levels start to stabilize",
    week4: "Mental clarity and focus peak",
    week9: "Sustained all-day energy becomes your new normal",
    footer: "We address the root causes of energy depletion, so you get a long-term solution, not just another quick fix."
  },
  lcarnitine: {
    title: "How will L-Carnitine work for you?",
    titleHighlight: "work for you?",
    week1: "Fat metabolism activation begins",
    week4: "Performance improvements become noticeable",
    week9: "Peak fat-burning and athletic performance",
    footer: "We address the root causes of metabolic slowdown, so you get a long-term solution, not just another quick fix."
  }
};

export default function TimelineScreen({ product, onContinue, onBack }) {
  const content = timelineContent[product?.id] || timelineContent.nad;

  // Chart data for the progression curve
  // Exponential growth for NAD+ (slower initial restore, accelerating later)
  const chartData = product?.id === 'nad'
    ? [
        { week: 0, health: 15, label: 'Week 0' },
        { week: 4, health: 40, label: 'Week 4' },
        { week: 12, health: 95, label: 'Week 12' }
      ]
    : [
        { week: 0, health: 20, label: 'Week 0' },
        { week: 4, health: 65, label: 'Week 4' },
        { week: 12, health: 95, label: 'Week 12' }
      ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-2 md:p-8">
        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-8">
          {content.title.split(content.titleHighlight)[0]}
          <span className="text-renuvia-gold">{content.titleHighlight}</span>
        </h2>

        {/* Timeline Chart */}
        <div className="bg-warm-beige rounded-xl p-6 md:p-8 mb-8">
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 55, right: 30, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  label={{
                    value: 'Energy & Cellular Health',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#64748b', fontSize: 11, textAnchor: 'middle' }
                  }}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                />
                <Line
                  type="monotone"
                  dataKey="health"
                  stroke="#059669"
                  strokeWidth={3}
                  dot={{ fill: '#059669', r: 6 }}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationBegin={300}
                />
                <ReferenceDot
                  x="Week 12"
                  y={95}
                  r={0}
                  label={<CustomLabel />}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Timeline milestones */}
          <div className="space-y-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[90px]">Week 1-4:</span>
              <span className="text-charcoal">{content.week1}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[90px]">Week 4-8:</span>
              <span className="text-charcoal">{content.week4}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[90px]">Week 9+:</span>
              <span className="text-charcoal">{content.week9}</span>
            </motion.div>
          </div>
        </div>

        {/* Footer message */}
        <p className="text-center text-charcoal mb-8">
          We address the <span className="text-renuvia-gold font-semibold">root causes</span> of cellular aging, so you get a <strong>long-term solution</strong>, not just another quick fix.
        </p>

        <div className="flex gap-4">
          <Button onClick={onBack} variant="ghost" className="w-1/4">
            ←
          </Button>
          <Button onClick={onContinue} variant="primary" className="w-3/4">
            Next →
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
